import { useEffect, useState } from "react";
import { Card, Col, Form, Input, Row, Tree } from "antd";

import api from "../../services/citizenScienceLookupService";
import { recordError } from "src/utils/logService";
import util from "../../utils/functions/custom";

const TargetBeneficiaries = ({
  formRef,
  formData,
  setBeneficiaries,
  beneficiaries,
  otherBeneficiary,
  setOtherBeneficiary,
}) => {
  const [form] = Form.useForm();
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [showOther, setShowOther] = useState(false);
  const [parentIds, setParentIds] = useState([]);
  const [selected, setSelected] = useState([]);

  var beneficiaryErrorMsg =
    "Please select at least one beneficiary option/sub-option";

  const formItemLayout = {
    labelCol: {
      span: 12,
    },
    wrapperCol: {
      span: 12,
    },
  };

  // console.log(beneficiaries, otherBeneficiary);
  useEffect(() => {
    getBeneficiaries();

    if (formRef != null) {
      formRef.current = form;
    }
  }, [form, formRef]);

  useEffect(() => {
    prePopulate();
  }, [treeData]);

  const getBeneficiaries = async () => {
    try {
      const response = await api.getBeneficiaries();
      if (response) {
        const { data } = response;
        const transData = transformData(data);
        setTreeData(transData);

        const parentIdNodes = transData.map((node) => node.key);
        const childNodes = collectChildKeys(transData);
        setParentIds(parentIdNodes);
        setExpandedKeys(childNodes);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const prePopulate = () => {
    if (!util.isEmpty(beneficiaries)) {
      let globalKeys = beneficiaries.filter((c) => c.impact.length ===0).map((k) => k.type);

      beneficiaries.filter((c) => c.impact.length > 0).forEach((c) => {
        globalKeys = globalKeys.concat(c.impact);
      });

      setCheckedKeys(globalKeys);
      const otherOption = treeData?.filter(n => n.title === "Other" && globalKeys.includes(n.key)).length !== 0;
      if (otherOption) {
        form.setFieldsValue({ otherBeneficiary:  otherBeneficiary });
        setShowOther(true);
      }else {
        form.setFieldsValue({ otherBeneficiary: null });
        setShowOther(false);
      }

      if (globalKeys.length > 0) {
        form.setFields([
          {
            name: "beneficiary",
            parent: globalKeys,
            value: globalKeys,
            errors: [],
          },
        ]);
      } else {
        form.setFields([
          {
            name: "beneficiary",
            value: [],
            errors: [beneficiaryErrorMsg],
          },
        ]);
      }
    }
  };

  const transformData = (data, isLeaf = false) => {
    return data.map((item) => ({
      key: item.key,
      title: item.value,
      isleaf: isLeaf,
      children: item.children
        ? transformData(item.children, (isLeaf = true))
        : [],
    }));
  };

  const collectChildKeys = (nodes) => {
    let keys = [];

    nodes.forEach((node) => {
      if (node.children && node.children.length > 0) {
        node.children.forEach((child) => keys.push(child.key.toString()));

        //? Line below is cateriing for inner child nodes
        keys = keys.concat(collectChildKeys(node.children));
      }
    });

    return keys;
  };

  const onExpand = (expandedKeysValue) => {
    setExpandedKeys(expandedKeysValue);
    setAutoExpandParent(false);
  };

  const onSelect = (selectedKeysValue, info) => {
    setSelectedKeys(selectedKeysValue);
  };

  const findChildBelongingByParent = (parentId, childId) => {
    const children = treeData.find((parent) => parent.key === parentId);
    if (children.some((c) => c.key === childId)) return true;
    return false;
  };

  const findParentByChildKey = (targetKey) => {
    for (let node of treeData) {
      if (
        node.children &&
        node.children.some((child) => child.key === targetKey)
      ) {
        return node;
      }
    }
    return null;
  };

  const onCheck = (checkedKeysValue, info) => {
    // console.log("checkedKeysValue: ", checkedKeysValue, info, selected);
    let newSelected = [...selected];

    if (checkedKeysValue.length === 0) {
      setSelected([]);
      setCheckedKeys(checkedKeysValue);
      form.setFieldsValue({ tree: checkedKeysValue });
      formRef?.current?.setFieldsValue({ beneficiary: checkedKeysValue });
      formRef?.current?.validateFields();
      return;
    }

    for (const node of info.checkedNodes) {
      const selectedItem = node.key;

      if (parentIds.includes(selectedItem)) {
        const foundParentKeys = treeData
          .find((c) => c.key === selectedItem)
          ?.children.map((c) => c.key);

        ///Filter out all other parents
        ///Filter children not belonging to current parent\
        const impact = checkedKeysValue.filter(
          (child) =>
            !parentIds.includes(child) && foundParentKeys.includes(child)
        );

        const exists = newSelected.find((c) => c.type === selectedItem);
        if (!exists) {
          newSelected.push({ type: selectedItem, impact });
        } else {
          exists.impact = impact;
        }
      } else {
        const parent = findParentByChildKey(selectedItem);

        if (parent) {
          let parentEntry = newSelected.find((c) => c.type === parent.key);
          if (parentEntry) {
            // Ensure that the selected items aer added that are not already in impact
            parentEntry.impact = parentEntry.impact.filter((impactItem) =>
              checkedKeysValue.includes(impactItem)
            );
            if (!parentEntry.impact.includes(selectedItem)) {
              parentEntry.impact.push(selectedItem);
            }
          } else {
            parentEntry = { type: parent.key, impact: [selectedItem] };
            newSelected.push(parentEntry);
          }
        }
      }
    }
    const _other = info.checkedNodes?.filter(n => n.title === "Other").length !== 0;
    setShowOther(_other);
    if (!_other) {
      form.setFieldsValue({ otherBeneficiary: null });
      setShowOther(false);
    }

    setSelected(newSelected);
    setCheckedKeys(checkedKeysValue);
    form.setFieldsValue({ tree: checkedKeysValue });
    setBeneficiaries(newSelected);

    if (checkedKeysValue.length > 0) {
      form.setFields([
        {
          name: "beneficiary",
          parent: checkedKeysValue,
          value: checkedKeysValue,
          errors: [],
        },
      ]);
    } else {
      form.setFields([
        {
          name: "beneficiary",
          value: [],
          errors: [beneficiaryErrorMsg],
        },
      ]);
    }
  };

  const validateTreeSelection = (_, value) => {
    if (!value || value.length === 0) {
      return Promise.reject(new Error(beneficiaryErrorMsg));
    }
    return Promise.resolve();
  };

  return (
    <Card
      className="project-card"
      title={
        <span style={{ color: "black", fontWeight: "bold", fontSize: "20px" }}>
          <b>STEP 5: Target Beneficiaries</b>
        </span>
      }
      bordered={false}
      headStyle={{ border: 0, textAlign: "left" }}
    >
      <p style={{ textAlign: "left" }}>
        Who are the <b>intended beneficiaries of this project</b> and what
        benefit do you intend for them to derive?
      </p>
      <Form
        form={form}
        layout="vertical"
        {...formItemLayout}
        initialValues={formData}
      >
        <Row gutter={16}>
          <Col xs={24} sm={24} md={18} lg={18} xl={18}>
            <Form.Item
              name="beneficiary"
              label="Beneficiaries"
              rules={[{ required: true, validator: validateTreeSelection }]}
            >
              <Tree
                checkable
                onExpand={onExpand}
                expandedKeys={expandedKeys}
                autoExpandParent={autoExpandParent}
                onCheck={onCheck}
                checkedKeys={checkedKeys}
                onSelect={onSelect}
                selectedKeys={selectedKeys}
                treeData={treeData}
              />
            </Form.Item>
          </Col>
        </Row>
        {showOther && (
          <Row>
            <Col xs={24} sm={24} md={18} lg={18} xl={18}>
              <Form.Item
                name="otherBeneficiary"
                label="Other Beneficiary"
                rules={[
                  {
                    required: true,
                    message: "Please enter other beneficiary",
                  },
                ]}
              >
                <Input onChange={(e) => setOtherBeneficiary(e.target.value)} />
              </Form.Item>
            </Col>
          </Row>
        )}

        <br />
      </Form>
    </Card>
  );
};

export default TargetBeneficiaries;
